import { useEffect, useState } from "react";
import { http, fmtINR, fmtNum } from "@/lib/api";

const STATUS_STYLES = {
    Match: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    Underreported: "bg-red-500/10 text-red-600 border-red-500/30",
    Overreported: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

export default function Reconciliation() {
    const [data, setData] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    useEffect(() => {
        const params = {};
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        http.get("/spykar/reconciliation", { params }).then((r) => setData(r.data));
    }, [fromDate, toDate]);

    const t = data?.totals;
    const rows = data?.rows || [];

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6" data-testid="reconciliation-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Spykar Ops · Reconciliation</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Daily vs POS Reconciliation</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Provisional daily sales (Fundle capture) vs final POS tertiary. Variance highlights under/over reporting.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-[11px]">
                        <span className="mlabel">From</span>
                        <input
                            type="date"
                            data-testid="recon-from-date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="rounded-sm border border-border bg-background px-2 py-1 text-[11px]"
                        />
                    </label>
                    <label className="flex items-center gap-1 text-[11px]">
                        <span className="mlabel">To</span>
                        <input
                            type="date"
                            data-testid="recon-to-date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="rounded-sm border border-border bg-background px-2 py-1 text-[11px]"
                        />
                    </label>
                </div>
            </div>

            {t && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <Stat label="Outlets Compared" value={fmtNum(t.outlets)} />
                    <Stat label="Matched" value={fmtNum(t.matched)} tone="green" />
                    <Stat label="Underreported" value={fmtNum(t.under)} tone="red" />
                    <Stat label="Overreported" value={fmtNum(t.over)} tone="amber" />
                    <Stat label="Absolute Variance" value={fmtINR(t.abs_variance_net)} tone="amber" />
                </div>
            )}

            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="recon-table">
                <div className="max-h-[calc(100vh-320px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left">Outlet</th>
                                <th className="px-3 py-2 text-left">City</th>
                                <th className="px-3 py-2 text-left">Region</th>
                                <th className="px-3 py-2 text-right">Daily Qty</th>
                                <th className="px-3 py-2 text-right">POS Qty</th>
                                <th className="px-3 py-2 text-right">Δ Qty</th>
                                <th className="px-3 py-2 text-right">Daily Net</th>
                                <th className="px-3 py-2 text-right">POS Net</th>
                                <th className="px-3 py-2 text-right">Δ Net</th>
                                <th className="px-3 py-2 text-right">Δ %</th>
                                <th className="px-3 py-2 text-left">Match</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.outlet} className="border-t border-border hover:bg-muted/40">
                                    <td className="px-3 py-1.5">{r.outlet}</td>
                                    <td className="px-3 py-1.5">{r.city || "—"}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{r.region || "—"}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.daily_qty)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.pos_qty)}</td>
                                    <td
                                        className={
                                            "mono-num px-3 py-1.5 text-right font-semibold " +
                                            (r.var_qty > 0 ? "text-red-600" : r.var_qty < 0 ? "text-amber-600" : "")
                                        }
                                    >
                                        {r.var_qty > 0 ? "+" : ""}
                                        {fmtNum(r.var_qty)}
                                    </td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.daily_net)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.pos_net)}</td>
                                    <td
                                        className={
                                            "mono-num px-3 py-1.5 text-right font-semibold " +
                                            (r.var_net > 0 ? "text-red-600" : r.var_net < 0 ? "text-amber-600" : "")
                                        }
                                    >
                                        {r.var_net > 0 ? "+" : ""}
                                        {fmtINR(r.var_net)}
                                    </td>
                                    <td className="mono-num px-3 py-1.5 text-right text-muted-foreground">
                                        {r.var_net_pct?.toFixed?.(1) ?? r.var_net_pct}%
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <span
                                            className={
                                                "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                (STATUS_STYLES[r.match_status] || "bg-muted text-muted-foreground border-border")
                                            }
                                        >
                                            {r.match_status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="px-3 py-10 text-center text-[12px] text-muted-foreground">
                                        No overlap between Daily Sales entries and POS Tertiary yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const c =
        tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}
